import React from "react";
import { isEmpty, isEqual } from "lodash";
import { moment, professions } from "@bonliva-crm/common";
import { Add, AddCircle, Info, Remove } from "@material-ui/icons";
import {
  Checkbox,
  Date,
  RadioGroup,
  Select,
  Text,
  ToggleButtonGroup
} from "@common/formx";
import SelectCommon from "@common/Select";
import locale, { dateFormat } from "@bonliva-crm/locale";
import {
  AddressInput,
  ConsultantContractAccommodationAlt,
  ConsultantContractBonus,
  ConsultantContractOvertimeOpt,
  ConsultantContractPayoutType,
  ConsultantContractTravelsAlt,
  ConsultantContractType,
  ContractsPriceTemplateType,
  ContractType,
  CustomerType,
  LegalPersonType
} from "@graphql";
import {
  calculatePriceOverSalaryLimit,
  calculatePriceUnderSalaryLimit,
  getConsultantContractAccAltOptions,
  isConfirmationContract,
  langOptions,
  YoExOptions
} from "@consultants/ConsultantContract/utils";
import { FieldProps as FieldPropsBase } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import yup from "yup";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";
import { DepartmentSelect, RegularButton } from "@common";
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup as MuiRadioGroup,
  Typography
} from "@material-ui/core";
import {
  defaultSalaryTypeOpt,
  IntroductionPriceInput,
  MaxCost,
  salaryTypeOption
} from "@consultants/ConsultantContract/PriceInputs";
import SwitchCommon from "@common/forms/common/Switch";
import Alert from "@material-ui/lab/Alert";
import { ContractFormStore, FormData } from "./ContractFormStore";
import { DnDLocalContainer } from "../../containers/DnDContainer/DnDLocalContainer";
import { FileUpload } from "mdi-material-ui";
import { FileList } from "@common/FileList/FileList";
import { FormSection } from "@common/forms";
import { WithTooltip } from "@components";
import { getConsultantContractTravelsAltOptions } from "./utils";
import { getExpandedArchivedGroup } from "../utils";
import { ContractsPriceTemplateEditorForm } from "../../config_editor/ContractsPriceTemplates/ContractsPriceEditorForm";
import WeeklyHoursWorkedBonus from "@consultants/components/Bonus/WeeklyHoursWorkedBonus";
import WorkedHourBonus from "@consultants/components/Bonus/WorkedHourBonus";
import OvertimeBonus from "@consultants/components/Bonus/OvertimeBonus";
import CustomBonus from "@consultants/components/Bonus/CustomBonus";
import {
  ANNUAL_PENSION_THRESHOLD_FALLBACK,
  ConsultantContractPensionSolution,
  ConsultantGetsPaidPer,
  Professions
} from "@bonliva-crm/shared";
import { toSEK } from "@utils/formatCurrency";

type FieldProps = FieldPropsBase<FormData, ContractFormStore>;

const field = ({
  name,
  order = 0,
  show = () => true,
  schema,
  isGroup = false,
  gridProps,
  xs = 4,
  md,
  render
}: FieldProps) => ({
  name,
  order,
  show,
  schema,
  isGroup,
  gridProps,
  xs,
  md,
  render
});

export const contractType: FieldProps = field({
  xs: 12,
  name: "ContractType",
  render: (form, { typeOptions, store }) => {
    return (
      <ToggleButtonGroup
        ButtonProps={{ disabled: store.action === "duplicate" }}
        name="ContractType"
        key="ContractType"
        label={locale.ContractType}
        options={typeOptions}
      />
    );
  },
  schema: yup
    .string()
    .required(locale.fieldIsMandatory(locale.ContractType))
    .nullable(true)
});

export const customerSelector: FieldProps = field({
  xs: 12,
  name: "HospitalRef",
  render: (form, { store: { setHospital, isParentKommun } }) => (
    <>
      <CustomerSelector
        key={"HospitalRef"}
        fullWidth
        label={locale["Customer name"]}
        error={form.getError("HospitalRef")}
        disabledFilterFields={["customerType"]}
        initialFilter={{
          customerType: CustomerType.Hospital
        }}
        onChange={(v) => setHospital(v)}
        value={form.values.HospitalRef}
      />
      {isParentKommun && (
        <SwitchCommon
          key={"showOrgName"}
          switchProps={{ color: "secondary" }}
          value={form.values.showOrgName}
          onChange={(v) => form.setValue("showOrgName", v)}
          label={locale.ShowOrgName}
        />
      )}
    </>
  ),
  schema: yup
    .object<LabelValue>()
    .shape({
      label: yup.string(),
      value: yup.string()
    })
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.Hospital))
});

export const departmentSelect: FieldProps = field({
  xs: 12,
  name: "DepartmentsRefs",
  show: ({ values }, { store: { allDepartments } }) =>
    !!(allDepartments && values.HospitalRef),
  render: (form, { store: { setDepartments, allDepartments } }) => (
    <>
      <DepartmentSelect
        key={"DepartmentsRefs"}
        clearable={false}
        multi={true}
        // optionType={"plain"}
        disabled={!form.values.HospitalRef}
        error={form.getError("DepartmentsRefs")}
        options={allDepartments}
        onChange={setDepartments}
        value={form.values.DepartmentsRefs}
      />
      <SwitchCommon
        key={"hideDeptName"}
        switchProps={{
          color: "secondary",
          disabled:
            !form.values.DepartmentsRefs || form.values.DepartmentsRefs?.length === 0
        }}
        value={form.values.hideDeptName}
        onChange={(v) => form.setValue("hideDeptName", v)}
        label={locale.hideDeptName}
      />
    </>
  ),
  schema: yup.string().nullable(true)
});

export const departmentSelect_national: FieldProps = field({
  xs: 12,
  name: "DepartmentsRefs",
  show: ({ values }, { store: { allDepartments } }) =>
    !!(allDepartments && values.HospitalRef),
  render: (form, { store: { setDepartments, allDepartments } }) => (
    <DepartmentSelect
      key={"DepartmentsRefs"}
      clearable={false}
      multi={true}
      // optionType={"plain"}
      disabled={!form.values.HospitalRef}
      error={form.getError("DepartmentsRefs")}
      options={allDepartments}
      onChange={setDepartments}
      value={form.values.DepartmentsRefs}
    />
  ),
  schema: yup.string().nullable(true)
});

export const address: FieldProps = field({
  xs: 12,
  name: "Address",
  show: ({ values }, { store: { isShowAdditionFields } }) =>
    values.HospitalRef && values.DepartmentsRefs?.length <= 1 && isShowAdditionFields,
  gridProps: { container: true, spacing: 2 },
  render: (form) => (
    <>
      <Grid item xs={12}>
        <SwitchCommon
          key={"ExcludeAddress"}
          switchProps={{ color: "secondary" }}
          value={form.values.ExcludeAddress}
          onChange={(v) => form.setValue("ExcludeAddress", v)}
          label={locale.ExcludeAddress}
        />
      </Grid>
      {!form.values.ExcludeAddress && (
        <>
          <Grid item xs={4}>
            <Text
              label={locale.City}
              name="Address.City"
              TextFieldProps={{ disabled: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <Text
              label={locale.Country}
              name="Address.Country"
              TextFieldProps={{ disabled: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <Text
              label={locale.StreetName}
              name="Address.StreetName"
              TextFieldProps={{ disabled: true }}
            />
          </Grid>
          <Grid item xs={4}>
            <Text
              label={locale.ZipCode}
              name="Address.ZipCode"
              TextFieldProps={{ disabled: true }}
            />
          </Grid>
        </>
      )}
    </>
  ),
  schema: yup
    .object<AddressInput>()
    .shape({
      City: yup.string(),
      Country: yup.string(),
      StreetName: yup.string(),
      ZipCode: yup.string()
    })
    .nullable(true)
});

export const address_national: FieldProps = field({
  xs: 12,
  name: "Address",
  show: ({ values }, { store: { isShowAdditionFields } }) =>
    values.HospitalRef && isShowAdditionFields,
  gridProps: { container: true, spacing: 2 },
  render: (form) => {
    const address = form?.values?.Address;

    const value = address
      ? [
          address.StreetName,
          address.City,
          address.ZipCode,
          address.Country,
          address.Region
        ]
          .filter((x) => x)
          .join(", ")
      : null;

    return (
      <Grid item xs={12}>
        <Typography>
          <strong>{locale.AddressToWorkplace}:</strong> <span>{value}</span>
        </Typography>
      </Grid>
    );
  },
  schema: yup
    .object<AddressInput>()
    .shape({
      City: yup.string(),
      Country: yup.string(),
      StreetName: yup.string(),
      ZipCode: yup.string()
    })
    .nullable(true)
});

export const startDate: FieldProps = field({
  name: "StartDate",
  show: (form, { store: { isShowDateFields } }) => isShowDateFields,
  schema: yup.string().nullable(true).required(locale.fieldIsMandatory(locale.StartDate)),
  render: () => (
    <Date
      utc
      key={"StartDate"}
      DatePickerProps={{
        startDate: moment().toDate(),
        shouldCloseOnSelect: true
      }}
      name="StartDate"
      // transformValue={v => moment(v).format("YYYY-MM-DD")}
      label={locale.StartDate}
    />
  )
});

export const signedOn: FieldProps = field({
  name: "SignedOn",
  xs: 8,
  schema: yup.string().nullable(true).required(" "),
  render: (form, { contractTypeToUpload }) => {
    const label =
      contractTypeToUpload === ConsultantContractType.Intermittent
        ? locale.DateIntermittentAgreementIsSigned
        : locale.DateFrameworkAgreementIsSigned;
    return (
      <Date
        utc
        key={"SignedOn"}
        DatePickerProps={{
          startDate: moment().toDate(),
          shouldCloseOnSelect: true,
          placeholder: label
        }}
        name="SignedOn"
        // transformValue={v => moment(v).format("YYYY-MM-DD")}
        label={label}
      />
    );
  }
});

export const salaryChoice: FieldProps = field({
  xs: 6,
  name: "SalaryChoice",
  show: (form, { store: { isSalaryChoiceRequired, isShowAdditionFields } }) => {
    return isSalaryChoiceRequired && isShowAdditionFields;
  },
  schema: yup // validation is on store validation function
    .string()
    .nullable(true),
  render: (form, { store: { consultantPension } }) => {
    let options = Object.values(ContractType).filter(
      (type) => type !== ContractType.NationalProcurement
    );

    if (consultantPension?.above60) {
      options = options.filter((v) => v !== ContractType.HourlyPensionInsurance);
    }

    const _opt = options.map((type) => ({
      label: locale.contractType[type],
      value: type
    }));

    return (
      <Select
        label={locale.SalaryChoice}
        name="SalaryChoice"
        key={"SalaryChoice"}
        clearable={false}
        multi={false}
        transformValue={(v) => v.value}
        fullWidth
        options={_opt}
      />
    );
  }
});

export const salaryChoice2: FieldProps = field({
  xs: 12,
  name: "SalaryChoice",
  schema: yup
    .string()
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.SalaryChoice)),
  render: () => {
    return (
      <Select
        label={locale.SalaryChoice}
        name="SalaryChoice"
        key={"SalaryChoice"}
        clearable={false}
        multi={false}
        transformValue={(v) => v.value}
        fullWidth
        options={Object.values(ContractType)
          .filter((type) => type !== ContractType.NationalProcurement)
          .map((type) => ({
            label: locale.contractType[type],
            value: type
          }))}
      />
    );
  }
});

export const endDate: FieldProps = field({
  name: "EndDate",
  show: (form, { store: { isShowDateFields } }) => isShowDateFields,
  schema: yup
    .string()
    .nullable(true)
    .test("EndDate", locale.EndDateAfterStartDate, function testStartDate(e) {
      const { StartDate: s } = this.parent;
      if (s && e) {
        return moment(e).isSameOrAfter(moment(s));
      }
      return true;
    })
    .required(locale.fieldIsMandatory(locale.EndDate)),
  render: ({ values }, { store }) => {
    return (
      <Date
        utc
        key={"EndDate"}
        DatePickerProps={{
          maxDate: store.maxEndDate ? moment(store.maxEndDate).toDate() : undefined,
          minDate: moment(values.StartDate).toDate(),
          startDate: moment().toDate(),
          shouldCloseOnSelect: true
        }}
        name="EndDate"
        label={locale.EndDate}
      />
    );
  }
});

export const pensionSolution: FieldProps = field({
  xs: 12,
  name: "pensionSolution",
  show: (
    form,
    { store: { isShowAdditionFields, consultantPension, currentConsultant } }
  ) =>
    isShowAdditionFields &&
    (consultantPension?.becomes25 ||
      consultantPension?.becomes65 ||
      form.values.pensionSolution === ConsultantContractPensionSolution.Split) &&
    currentConsultant?.LegalPersonType === LegalPersonType.Employee,
  schema: yup.mixed(),
  render: ({ values: formValues, errors }, { store }) => {
    const approximateAge = moment(formValues.EndDate, dateFormat).diff(
      store.currentConsultant.BirthDate,
      "years"
    );

    const years = approximateAge >= 64 ? 65 : 24;
    const { getLabel, getValues, warningText } = locale.consultantContractPensionSolution;
    const values = getValues(years);
    const label = getLabel(years);

    return (
      <>
        {formValues.pensionSolution === ConsultantContractPensionSolution.Split ? (
          <FormHelperText error style={{ fontSize: "1rem" }}>
            {warningText}
          </FormHelperText>
        ) : null}
        <FormControl>
          {!errors.pensionSolution ? (
            <FormLabel component="legend">{label}</FormLabel>
          ) : null}
          <RadioGroup
            name="pensionSolution"
            onChange={(e) =>
              store.setPensionSolution(
                e.target.value as ConsultantContractPensionSolution
              )
            }
            options={Object.values(ConsultantContractPensionSolution).map((v) => ({
              label: values[v],
              value: v
            }))}
          />
        </FormControl>
      </>
    );
  }
});

export const period: FieldProps = field({
  xs: 12,
  name: "Period",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup
    .string()
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.PeriodDesc)),
  render: () => (
    <Text
      key={"Period"}
      label={locale.PeriodDesc}
      name="Period"
      TextFieldProps={{
        multiline: true,
        helperText: locale["Press Enter for a new line"]
      }}
    />
  )
});

export const education: FieldProps = field({
  xs: 12,
  name: "Education",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup.object().shape({
    Show: yup.boolean(),
    Text: yup.string().when("Show", {
      is: (v) => !!v,
      then: (s) => s.required(locale.EducationClarification)
    })
  }),
  render: (form) => (
    <div key={"Education"}>
      <SwitchCommon
        switchProps={{ color: "secondary" }}
        value={form.values.Education.Show}
        onChange={(v) => form.setValue("Education.Show", v)}
        label={locale.Education}
      />
      {form.values.Education.Show && (
        <Text label={locale.EducationClarification} name="Education.Text" />
      )}
    </div>
  )
});

export const driverLicense: FieldProps = field({
  xs: 12,
  name: "DriverLicense",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup.string().nullable(true),
  render: (form) => (
    <SwitchCommon
      key={"DriverLicense"}
      switchProps={{ color: "secondary" }}
      value={form.values.DriverLicense}
      onChange={(v) => form.setValue("DriverLicense", v)}
      label={locale.DriverLicense}
    />
  )
});

export const notRelevantMonthlyWeeklyPayouts: FieldProps = field({
  xs: 12,
  name: "NotRelevantMonthlyWeeklyPayouts",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup.string().nullable(true),
  render: (form) => (
    <SwitchCommon
      key={"NotRelevantMonthlyWeeklyPayouts"}
      switchProps={{ color: "secondary" }}
      value={form.values.NotRelevantMonthlyWeeklyPayouts}
      onChange={(v) => form.setValue("NotRelevantMonthlyWeeklyPayouts", v)}
      label={
        form.values.Profession === Professions.SE_PSYKOLOG
          ? locale.NotRelevantMonthlyWeeklyPayoutsForPsy
          : locale.NotRelevantMonthlyWeeklyPayouts
      }
    />
  )
});

export const schedule: FieldProps = field({
  xs: 12,
  name: "Schedule",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup.string().nullable(true),
  render: () => (
    <Text
      key={"Schedule"}
      label={locale.Schedule}
      name="Schedule"
      TextFieldProps={{
        multiline: true,
        helperText: locale["Press Enter for a new line"]
      }}
    />
  )
});

export const patientVisits: FieldProps = field({
  xs: 12,
  name: "PatientVisits",
  show: (form, { store: { isShowAdditionFields, tenant } }) =>
    isShowAdditionFields && tenant !== "wcc",
  schema: yup.number().positive().nullable(true),
  render: () => (
    <Text
      key={"PatientVisits"}
      label={locale.NumberOfVisitsDesc}
      name="PatientVisits"
      TextFieldProps={{ type: "number" }}
    />
  )
});

export const investigationsNumber: FieldProps = field({
  xs: 12,
  name: "InvestigationsNumber",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup.number().positive().nullable(true),
  render: () => (
    <Text
      key={"InvestigationsNumber"}
      label={locale.InvestigationsNumber}
      name="InvestigationsNumber"
      TextFieldProps={{ type: "number" }}
    />
  )
});

export const otherRequirements: FieldProps = field({
  xs: 12,
  name: "OtherRequirements",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup.array().of(yup.string()),
  render: ({ values }, { store: { addRequirements, removeRequirements } }) => (
    <FormControl fullWidth key={"OtherRequirements"}>
      <FormLabel component="legend">
        {`${locale.OtherSpecialRequirements} ${locale.OtherSpecialRequirementsDesc}`}
      </FormLabel>
      {values.OtherRequirements?.map((el, i) => (
        <Grid key={i} item container spacing={2} xs={12}>
          <Grid item xs={6}>
            <Text
              name={`OtherRequirements[${i}]`}
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <IconButton
              aria-label="add"
              onClick={() => addRequirements("OtherRequirements")}
            >
              <Add fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="delete"
              disabled={values.OtherRequirements?.length === 1}
              onClick={() => removeRequirements("OtherRequirements", i)}
            >
              <Remove fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </FormControl>
  )
});

export const experienceYears: FieldProps = field({
  xs: 6,
  name: "YoEx",
  schema: yup.string().nullable(true),
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  render: () => (
    <Select
      label={locale.ExperienceYears}
      name="YoEx"
      key="YoEx"
      clearable={false}
      multi={false}
      transformValue={(v) => v.value}
      options={YoExOptions}
    />
  )
});

export const profession: FieldProps = field({
  xs: 12,
  name: "Profession",
  schema: yup
    .string()
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.Profession)),
  render: (form, { store: { allProfessions } }) => (
    <Select
      label={locale.Profession}
      name="Profession"
      key={"Profession"}
      clearable={false}
      multi={false}
      transformValue={(v) => v.value}
      options={allProfessions.toLabelValue(professions.getProfessionDisplayName)}
    />
  )
});

export const country: FieldProps = field({
  xs: 6,
  name: "Country",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup
    .string()
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.CountryInWhichTheConsultantWillWork)),
  render: (form, { store: { allCountries } }) => (
    <Select
      label={locale.CountryInWhichTheConsultantWillWork}
      name="Country"
      options={allCountries.map((v) => ({
        label: locale.countries[v],
        value: v
      }))}
    />
  )
});

export const professionDisabled: FieldProps = {
  ...profession,
  render: (form, { store: { allProfessions } }) => (
    <Select
      label={locale.Profession}
      name="Profession"
      key={"Profession"}
      disabled
      clearable={false}
      multi={false}
      transformValue={(v) => v.value}
      options={allProfessions}
    />
  )
};

export const areaOfExpertise: FieldProps = field({
  xs: 12,
  name: "AreaOfExpertise",
  schema: yup
    .string()
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.AreasOfExpertise)),
  gridProps: { container: true },
  render: (form, { store: { allAreasOfExpertise } }) => (
    <Grid item xs={6}>
      <Select
        key="AreaOfExpertise"
        label={locale.AreaOfExpertise}
        name="AreaOfExpertise"
        multi={false}
        options={allAreasOfExpertise.toLabelValue()}
      />
    </Grid>
  )
});

export const specializations: FieldProps = field({
  xs: 12,
  name: "Specializations",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  schema: yup.array().of(yup.string()),
  render: (form, { store: { allSpecializations } }) => (
    <Select
      label={locale.SpecializationsRequirements}
      key="Specializations"
      name="Specializations"
      multi
      options={allSpecializations}
    />
  )
});

export const language: FieldProps = field({
  xs: 12,
  name: "Language",
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  render: () => (
    <RadioGroup
      name="Language"
      key={"Language"}
      label={locale.Language}
      options={langOptions}
    />
  )
});

export const payoutTypeEmp: FieldProps = field({
  xs: 12,
  name: "PayoutType",
  schema: yup
    .mixed()
    .oneOf(
      Object.values(ConsultantContractPayoutType),
      locale.fieldIsMandatory(locale.Payout)
    ),
  render: () => (
    <RadioGroup
      name="PayoutType"
      key={"PayoutType"}
      label={locale.Payout}
      options={Object.values(ConsultantContractPayoutType).map((v) => ({
        label: locale.consultantContractPayoutType[v],
        value: v
      }))}
    />
  )
});

export const consultantGetsPaidPer: FieldProps = field({
  xs: 12,
  name: "ConsultantGetsPaidPer",
  show: () => false,
  schema: yup
    .mixed()
    .oneOf(
      Object.values(ConsultantGetsPaidPer),
      locale.fieldIsMandatory(locale.ConsultantGetsPaidPer)
    ),
  render: () => (
    <RadioGroup
      name="ConsultantGetsPaidPer"
      key={"ConsultantGetsPaidPer"}
      disabled
      label={locale.ConsultantGetsPaidPer}
      options={Object.values(ConsultantGetsPaidPer).map((v) => ({
        label: locale.consultantGetsPaidPer[v],
        value: v
      }))}
    />
  )
});

export const isRegionDalarna: FieldProps = field({
  xs: 12,
  name: "IsRegionDalarna",
  schema: yup.mixed(),
  render: () => (
    <Checkbox
      name={"IsRegionDalarna"}
      CheckboxProps={{
        style: { marginLeft: 16 }
      }}
      label={
        <WithTooltip
          title={
            "När denna checkbox är i klickad så bifogas en paragraf som berör leverantörer som kan komma att på avtalet för Region Dalarna. Texten säger att leverantören måste höra av sig till oss senast vecka 37 respektive vecka 12 om en konsult inte har för avsikt att fortsätta arbeta för oss på detta avtal"
          }
        >
          <span>
            Region Dalarna
            <Info style={{ marginBottom: -5 }} />
          </span>
        </WithTooltip>
      }
    />
  )
});

export const payoutTypeSub: FieldProps = field({
  xs: 12,
  name: "PayoutType",
  render: () => (
    <RadioGroup
      name="PayoutType"
      key={"PayoutType"}
      label={locale.Payout}
      options={Object.values(ConsultantContractPayoutType).map((v) => ({
        label: locale.consultantContractPayoutType[v],
        value: v
      }))}
    />
  )
});

export const overtime: FieldProps = field({
  xs: 12,
  name: "Overtime",
  show: (form, { store: { isShowAdditionFields, hideOvertimeForNPFullStrategy } }) =>
    isShowAdditionFields && !hideOvertimeForNPFullStrategy,
  render: () => (
    <RadioGroup
      name="Overtime"
      key={"Overtime"}
      label={locale.Overtime}
      options={Object.values(ConsultantContractOvertimeOpt).map((v) => ({
        label: locale.consultantContractOvertimeOpt[v],
        value: v
      }))}
    />
  )
});

export const specificRequirements: FieldProps = field({
  xs: 12,
  name: "SpecificRequirements",
  schema: yup.array().of(yup.string()),
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  render: ({ values }, { store: { addRequirements, removeRequirements } }) => (
    <FormControl fullWidth key={"SpecificRequirements"}>
      <FormLabel component="legend">
        {`${locale.SpecificRequirementsForTheCompetence} ${locale.SpecificRequirementsForTheCompetenceDesc}`}
      </FormLabel>
      {values.SpecificRequirements?.map((el, i) => (
        <Grid key={i} item container spacing={2} xs={12}>
          <Grid item xs={6}>
            <Text
              name={`SpecificRequirements[${i}]`}
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <IconButton
              aria-label="add"
              onClick={() => addRequirements("SpecificRequirements")}
            >
              <Add fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="delete"
              disabled={values.SpecificRequirements?.length === 1}
              onClick={() => removeRequirements("SpecificRequirements", i)}
            >
              <Remove fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </FormControl>
  )
});

export const specificRequirementsBConfirmationEmp: FieldProps = field({
  xs: 12,
  name: "SpecificRequirements",
  schema: yup.array().of(yup.string()),
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  render: ({ values }, { store: { addRequirements, removeRequirements } }) => (
    <FormControl fullWidth key={"SpecificRequirements"}>
      <FormLabel component="legend">
        {locale.SpecificRequirementsForBookingConfirmationEmp}
      </FormLabel>
      {values.SpecificRequirements?.map((el, i) => (
        <Grid key={i} item container spacing={2} xs={12}>
          <Grid item xs={6}>
            <Text
              name={`SpecificRequirements[${i}]`}
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <IconButton
              aria-label="add"
              onClick={() => addRequirements("SpecificRequirements")}
            >
              <Add fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="delete"
              disabled={values.SpecificRequirements?.length === 1}
              onClick={() => removeRequirements("SpecificRequirements", i)}
            >
              <Remove fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </FormControl>
  )
});

export const bankCounselling: FieldProps = field({
  xs: 12,
  name: "bankCounselling",
  show: (form) => form.values.SalaryChoice === ContractType.Hourly,
  schema: yup.mixed(),
  render: () => (
    <RadioGroup
      name="bankCounselling"
      key="bankCounselling"
      label={locale.BankCounselling}
      options={[
        {
          label: locale.Yes,
          value: "true"
        },
        {
          label: locale.No,
          value: "false"
        }
      ]}
    />
  )
});

export const bonusRecords: FieldProps = field({
  xs: 12,
  name: "Bonus",
  show: () => false,
  schema: yup.array().of(
    yup.lazy((item: ConsultantContractBonus) => {
      if (!item) return yup.mixed();

      if (isEqual(item.Type, "WeeklyHoursWorkedBonus")) {
        return WeeklyHoursWorkedBonus.ValidationSchema(true);
      } else if (isEqual(item.Type, "WorkedHourBonus")) {
        return WorkedHourBonus.ValidationSchema(true);
      } else if (isEqual(item.Type, "OvertimeBonus")) {
        return OvertimeBonus.ValidationSchema(true);
      } else if (isEqual(item.Type, "CustomBonus")) {
        return CustomBonus.ValidationSchema(true);
      }

      return yup.mixed();
    })
  ),
  render: (
    form,
    {
      store: {
        bonusTypes,
        addBonusRecord,
        removeBonusRecord,
        handleChangeBonusType,
        isNationalProcurementPriceStrategy
      }
    }
  ) => {
    return (
      <FormControl fullWidth error={!!form.errors.BasicPrice} key={"Bonus"}>
        <FormLabel component="legend">{locale.Bonuses}</FormLabel>
        <Grid container spacing={2} xs={12} alignItems={"center"}>
          <Grid item xs={2}>
            <SelectCommon
              fullWidth
              clearable
              name={`bonusType`}
              key={`bonusType`}
              label={locale.BonusType}
              value={form.values.bonusType}
              onChange={handleChangeBonusType}
              options={bonusTypes?.map((x) => ({ label: locale.bonuses[x], value: x }))}
            />
          </Grid>
          <Grid item xs={2}>
            <Button disabled={!form.values.bonusType} onClick={() => addBonusRecord()}>
              {locale.AddBonus}
            </Button>
          </Grid>

          {form.values.Bonus?.length > 0 ? (
            <Grid item xs={12}>
              <Divider style={{ margin: "5px 0" }} />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            {form.values.Bonus?.map((bonusData, i) => {
              if (isEqual(bonusData.Type, "WeeklyHoursWorkedBonus")) {
                return (
                  <WeeklyHoursWorkedBonus
                    index={i}
                    withSalaryType={true}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                    isNationalProcurementPriceStrategy={
                      isNationalProcurementPriceStrategy
                    }
                  />
                );
              } else if (isEqual(bonusData.Type, "WorkedHourBonus")) {
                return (
                  <WorkedHourBonus
                    index={i}
                    withSalaryType={true}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                    isNationalProcurementPriceStrategy={
                      isNationalProcurementPriceStrategy
                    }
                  />
                );
              } else if (isEqual(bonusData.Type, "OvertimeBonus")) {
                return (
                  <OvertimeBonus
                    index={i}
                    withSalaryType={true}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                    isNationalProcurementPriceStrategy={
                      isNationalProcurementPriceStrategy
                    }
                  />
                );
              } else if (isEqual(bonusData.Type, "CustomBonus")) {
                return (
                  <CustomBonus
                    index={i}
                    withSalaryType={true}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                    isNationalProcurementPriceStrategy={
                      isNationalProcurementPriceStrategy
                    }
                  />
                );
              }

              return null;
            })}
          </Grid>
        </Grid>
      </FormControl>
    );
  }
});

export const pricesRecords: FieldProps = field({
  xs: 12,
  name: "SalaryRecords",
  show: () => false,
  isGroup: true,
  schema: {
    SalaryRecords: yup
      .array()
      .of(
        yup.object().shape({
          deleted: yup.boolean().required()
        })
      )
      .test(
        "at-least-one-not-deleted",
        "At least one item must have deleted: false",
        (value) => {
          if (!Array.isArray(value) || value.length === 0) {
            return false; // Array is empty, so validation fails
          }
          return value.some((item) => item.deleted === false);
        }
      ),
    BasicPrice: yup
      .number()
      .nullable(true)
      .required(locale.fieldIsMandatory(locale.SalaryCompensation)),
    SpecificPeriodPrices: yup.array().of(
      yup.object().shape({
        price: yup
          .number()
          .nullable(true)
          .required(locale.fieldIsMandatory(locale.Addition)),
        startDate: yup
          .string()
          .nullable(true)
          .required(locale.fieldIsMandatory(locale.StartDate)),
        endDate: yup
          .string()
          .nullable(true)
          .test("EndDate", locale.EndDateAfterStartDate, function testStartDate(e) {
            const { startDate: s } = this.parent;
            if (s && e) {
              return moment(e).isSameOrAfter(moment(s));
            }
            return true;
          })
          .required(locale.fieldIsMandatory(locale.EndDate))
      })
    ),
    IntroductionPrice: yup
      .array()
      .of(
        yup.object().shape({
          Sum: yup.number().nullable(true).required(locale.fieldIsMandatory(locale.Sum)),
          Description: yup
            .string()
            .nullable(true)
            .required(locale.fieldIsMandatory(locale.Description))
        })
      )
      .nullable(true)
  },
  render: (
    form,
    {
      store: {
        addSalary,
        duplicateSalary,
        pushToArr,
        removeByIndex,
        handleChangeTemplate,
        defaultTemplateWarning,
        disabledSalaryRecord,
        handleChangeFavTemplate,
        handleChangeCustomerTemplate,
        templates,
        customerPriceOptions,
        fav,
        diffBetweenCustomerTemplate,
        remSpecificPeriodPrice,
        addSpecificPeriodPrice,
        validationResult,
        isBCContractTypeSelected,
        basicPriceLabel,
        isNationalProcurementPriceStrategy,
        getCurrentYearPayoutForNationalProcurement
      }
    }
  ) => {
    // const disableAddSalary =
    //   form?.values.Overtime === ConsultantContractOvertimeOpt.SKLBasedOvertime;
    const thisYearPayout = getCurrentYearPayoutForNationalProcurement();
    const isNotHourlyBC =
      !isBCContractTypeSelected ||
      form.values.ConsultantGetsPaidPer !== ConsultantGetsPaidPer.Investigation;

    return (
      <>
        {diffBetweenCustomerTemplate && form.values.customerTemplate && (
          <Alert style={{ margin: "10px 0" }} severity="warning">
            {locale.PriceListDiffFromCustomerTemplate}
          </Alert>
        )}
        {defaultTemplateWarning && (
          <Alert style={{ margin: "10px 0" }} severity="warning">
            {defaultTemplateWarning}
          </Alert>
        )}
        {form.getError("noDefaultSalaryTemplate") && (
          <Alert style={{ margin: "10px 0" }} severity="error">
            {form.getError("noDefaultSalaryTemplate")}
          </Alert>
        )}
        <FormControl fullWidth error={!!form.errors.BasicPrice} key={"Salary"}>
          <FormLabel component="legend">{locale.SalaryCompensation}</FormLabel>
          <SelectCommon
            // clearable={false}
            name={`template`}
            disabled={disabledSalaryRecord}
            key={`template`}
            label={locale.Template}
            fullWidth
            value={form.values.template}
            onChange={handleChangeTemplate}
            options={templates?.map((x) => ({ label: x.name, value: x._id }))}
          />
          <SelectCommon
            // clearable={false}
            name={`favTemplate`}
            disabled={disabledSalaryRecord}
            key={`favTemplate`}
            label={locale.FavTemplate}
            fullWidth
            value={form.values.favTemplate}
            onChange={handleChangeFavTemplate}
            options={fav?.map((x) => ({ label: x.name, value: x._id }))}
          />
          <SelectCommon
            showSearchInput={false}
            disablePortal={false}
            label={locale.ConnectedCustomer}
            fullWidth
            nested
            groupBy="parent"
            onChange={handleChangeCustomerTemplate}
            options={customerPriceOptions}
            value={form.values.customerTemplate}
            expanded={getExpandedArchivedGroup(
              form.values.customerTemplate,
              customerPriceOptions
            )}
          />
          <FormGroup>
            <Text
              type="number"
              name={`BasicPrice`}
              label={basicPriceLabel}
              TextFieldProps={{
                inputMode: "numeric"
              }}
            />
            {isNationalProcurementPriceStrategy && (
              <Grid container>
                <Grid item xs={12} style={{ marginTop: "10px" }}>
                  <Typography>
                    {locale.BasicPriceUnderSalaryLimit}:{" "}
                    <b>
                      {calculatePriceUnderSalaryLimit(form.values.BasicPrice, "kr/Tim")}
                    </b>
                  </Typography>
                </Grid>
                <Grid item xs={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
                  <Typography>
                    {locale.BasicPriceOverSalaryLimit}:{" "}
                    <b>
                      {calculatePriceOverSalaryLimit(form.values.BasicPrice, "kr/Tim")}
                    </b>
                  </Typography>
                </Grid>
              </Grid>
            )}
            {isNationalProcurementPriceStrategy && thisYearPayout < 200000 ? (
              <Grid item xs={12} style={{ marginTop: "10px" }}>
                <Alert severity="warning">
                  Konsulten har tjänat mindre än 200 000 kr innevarande år.
                </Alert>
              </Grid>
            ) : null}

            {isNationalProcurementPriceStrategy &&
            thisYearPayout > 200000 &&
            thisYearPayout < ANNUAL_PENSION_THRESHOLD_FALLBACK ? (
              <Grid item xs={12} style={{ marginTop: "10px" }}>
                <Alert severity="warning">
                  Konsulten har tjänat {toSEK(thisYearPayout ?? 0)} kr innevarande år.
                  Glöm inte att meddela konsulten ifall du tror att hen kommer passera{" "}
                  {toSEK(ANNUAL_PENSION_THRESHOLD_FALLBACK)} under kommande avtalsperiod!
                </Alert>
              </Grid>
            ) : null}

            {isNationalProcurementPriceStrategy &&
            thisYearPayout >= ANNUAL_PENSION_THRESHOLD_FALLBACK ? (
              <Grid item xs={12} style={{ marginTop: "10px" }}>
                <Alert severity="warning">
                  Konsulten har tjänat mer är 7,5*Inkomstbasbelopp under innevarande år.
                  Glöm inte göra konsult uppmärksam på att hen då får den lägre lönen.
                </Alert>
              </Grid>
            ) : null}

            {form.values.SpecificPeriodPrices?.length > 0 ? (
              <Grid container spacing={2}>
                {form.values.SpecificPeriodPrices.map((x, i) => {
                  const name = `SpecificPeriodPrices[${i}]`;
                  return (
                    <React.Fragment key={name}>
                      <Grid item xs={2}>
                        <Text
                          name={`${name}.price`}
                          label={
                            isNationalProcurementPriceStrategy
                              ? locale.AdditionNationalProcurementPriceStrategy
                              : locale.Addition
                          }
                          TextFieldProps={{
                            type: "number",
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position={"start"}>
                                  <Add />
                                </InputAdornment>
                              )
                            }
                          }}
                        />
                      </Grid>
                      {isNationalProcurementPriceStrategy && (
                        <Grid item xs={1} style={{ minWidth: "180px" }}>
                          <div>
                            {locale.FixedPrice + " <7,5: "}
                            <b>
                              {calculatePriceUnderSalaryLimit(
                                form.getValue(`${name}.price`),
                                "kr"
                              )}
                            </b>
                          </div>
                          <div>
                            {locale.FixedPrice + " >7,5: "}
                            <b>
                              {calculatePriceOverSalaryLimit(
                                form.getValue(`${name}.price`),
                                "kr"
                              )}
                            </b>
                          </div>
                        </Grid>
                      )}
                      <Grid item xs={1}>
                        <Text name={`${name}.title`} label={locale.Title} />
                      </Grid>
                      <Grid item xs={3}>
                        <Date
                          utc
                          key={"StartDate"}
                          DatePickerProps={{
                            minDate: moment(form.values.StartDate).toDate(),
                            maxDate: moment(form.values.EndDate).toDate(),
                            shouldCloseOnSelect: true
                          }}
                          name={`${name}.startDate`}
                          label={locale.StartDate}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Date
                          key={"EndDate"}
                          DatePickerProps={{
                            minDate: moment(form.values.StartDate).toDate(),
                            maxDate: moment(form.values.EndDate).toDate(),
                            startDate: moment().toDate(),
                            shouldCloseOnSelect: true
                          }}
                          name={`${name}.endDate`}
                          label={locale.EndDate}
                        />
                      </Grid>
                      <Grid item xs={1} style={{ padding: "10px 0" }}>
                        <IconButton onClick={() => remSpecificPeriodPrice(i)}>
                          <Remove />
                        </IconButton>
                      </Grid>
                    </React.Fragment>
                  );
                })}
              </Grid>
            ) : isNotHourlyBC ? (
              <Grid item xs={3} style={{ padding: "10px 0" }}>
                <Button
                  onClick={addSpecificPeriodPrice}
                  color={"secondary"}
                  variant={"contained"}
                  startIcon={<AddCircle />}
                >
                  {locale.AddAdditionPeriodPrice}
                </Button>
              </Grid>
            ) : null}

            <ContractsPriceTemplateEditorForm<FormData>
              formStore={form}
              duplicateRecord={(item) => duplicateSalary(item)}
              addBlankRecord={addSalary}
              type={ContractsPriceTemplateType.ConsultantContract}
              name={`SalaryRecords`}
              holidaysName={"highHolidays"}
              variant={"consultantContract"}
              validationResult={validationResult}
              disabled={disabledSalaryRecord}
              hideHolidays={!isNotHourlyBC}
              isNationalProcurementPriceStrategy={isNationalProcurementPriceStrategy}
              isBonlivaCare={isBCContractTypeSelected}
            />
          </FormGroup>
        </FormControl>
        {diffBetweenCustomerTemplate && form.values.customerTemplate && (
          <Alert style={{ margin: "10px 0" }} severity="warning">
            {locale.PriceListDiffFromCustomerTemplate}
          </Alert>
        )}
        {isNotHourlyBC ? (
          <IntroductionPriceInput
            name={"IntroductionPrice"}
            // error={form.getError("IntroductionPrice")}
            form={form}
            add={pushToArr}
            remove={removeByIndex}
            isNationalProcurementPriceStrategy={isNationalProcurementPriceStrategy}
          />
        ) : null}
      </>
    );
  }
});

export const nationalProcurementPriceStrategy: FieldProps = field({
  xs: 12,
  order: -1,
  name: "NationalProcurementPriceStrategy",
  show: () => false,
  isGroup: true,
  schema: {},
  render: (form) => {
    const isConfirmation = isConfirmationContract(form.values.ContractType);
    return (
      <p>
        <FormControl disabled={isConfirmation}>
          <FormLabel>{locale.NationalProcurementPriceStrategyQuestion}</FormLabel>
          <MuiRadioGroup
            name="isNationalProcurementPriceStrategy"
            value={
              form.values.isNationalProcurementPriceStrategy === null
                ? null
                : form.values.isNationalProcurementPriceStrategy
                ? "true"
                : "false"
            }
            onChange={(e) =>
              form.setValue(
                "isNationalProcurementPriceStrategy",
                e.target.value === "true"
              )
            }
          >
            <FormControlLabel
              value="true"
              style={{ marginTop: 10 }}
              control={<Radio />}
              label={locale.NationalProcurementPriceStrategyYes}
              disabled={isConfirmation}
            />
            <FormControlLabel
              value="false"
              control={<Radio />}
              label={locale.NationalProcurementPriceStrategyNo}
              disabled={isConfirmation}
            />
          </MuiRadioGroup>
        </FormControl>
      </p>
    );
  }
});

export const accSubSchema = yup.object().shape({
  Type: yup
    .mixed()
    .oneOf(
      Object.values(ConsultantContractAccommodationAlt),
      locale.fieldIsMandatory(locale.Accommodation)
    )
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.Accommodation)),
  MaxCost: yup.object().when("Type", {
    is: (t) => {
      return (
        t === ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowance ||
        t ===
          ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowanceAgainstReceipt
      );
    },
    then: (s) => {
      return s.test(
        "MaxCost",
        locale.fieldIsMandatory(locale.MaxCost),
        (v) => v.Price > 0 && !isEmpty(v.Currency) && !isEmpty(v.Period)
      );
    }
  })
});

export const accSubSchemaBC = yup.object().shape({
  Type: yup.mixed().nullable(true),
  MaxCost: yup.object().when("Type", {
    is: (t) => {
      return (
        t === ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowance ||
        t ===
          ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowanceAgainstReceipt
      );
    },
    then: (s) => {
      return s.test(
        "MaxCost",
        locale.fieldIsMandatory(locale.MaxCost),
        (v) => v.Price > 0 && !isEmpty(v.Currency) && !isEmpty(v.Period)
      );
    }
  })
});

export const accSchemaBC = yup.object().shape({
  Type: yup.mixed().nullable(true),
  MaxCost: yup.object().when("Type", {
    is: (t) => {
      return (
        t === ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowance ||
        t ===
          ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowanceAgainstReceipt
      );
    },
    then: (s) => {
      return s.test(
        "MaxCost",
        locale.fieldIsMandatory(locale.MaxCost),
        (v) =>
          v.Price > 0 &&
          !isEmpty(v.Currency) &&
          !isEmpty(v.Period) &&
          !isEmpty(v.SalaryType)
      );
    }
  })
});

export const accommodation: FieldProps = field({
  xs: 12,
  name: "Accommodation",
  schema: yup.object().shape({
    Type: yup
      .mixed()
      .oneOf(
        Object.values(ConsultantContractAccommodationAlt),
        locale.fieldIsMandatory(locale.Accommodation)
      )
      .nullable(true)
      .required(locale.fieldIsMandatory(locale.Accommodation)),
    MaxCost: yup.object().when("Type", {
      is: (t) => {
        return (
          t === ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowance ||
          t ===
            ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowanceAgainstReceipt
        );
      },
      then: (s) => {
        return s.test(
          "MaxCost",
          locale.fieldIsMandatory(locale.MaxCost),
          (v) =>
            v.Price > 0 &&
            !isEmpty(v.Currency) &&
            !isEmpty(v.Period) &&
            !isEmpty(v.SalaryType)
        );
      }
    })
  }),
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  render: (form, { store: { currentConsultant, tenant } }) => {
    const isSubcontractor =
      currentConsultant?.LegalPersonType === LegalPersonType.Subcontractor;
    const salaryTypeOpt =
      form.values.Accommodation?.Type ===
        ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowanceAgainstReceipt &&
      isSubcontractor
        ? defaultSalaryTypeOpt
        : salaryTypeOption;
    const showMaxCostAccommodation =
      form.values.Accommodation?.Type ===
        ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowance ||
      form.values.Accommodation?.Type ===
        ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowanceAgainstReceipt;
    const options = getConsultantContractAccAltOptions(tenant, isSubcontractor);

    return (
      <FormControl fullWidth key="Accommodation">
        <FormLabel component="legend">{locale.Accommodation}</FormLabel>
        <FormGroup>
          <RadioGroup name="Accommodation.Type" options={options} />
          {form.values.Accommodation?.Type ===
            ConsultantContractAccommodationAlt.BonlivaPayAccommodation && (
            <Grid item xs={12}>
              <Text
                name="Accommodation.PossibleSupplement"
                label={locale.PossibleSupplement}
                TextFieldProps={{
                  multiline: true,
                  helperText: locale["Press Enter for a new line"]
                }}
              />
            </Grid>
          )}
          {showMaxCostAccommodation && (
            <Grid item xs={6}>
              <MaxCost
                name="Accommodation.MaxCost"
                error={form.getError("Accommodation.MaxCost")}
                contractVariant={currentConsultant?.LegalPersonType}
                options={salaryTypeOpt}
              />
            </Grid>
          )}
        </FormGroup>
      </FormControl>
    );
  }
});

export const travelsSubSchema = yup.object().shape({
  Type: yup
    .mixed()
    .oneOf(
      Object.values(ConsultantContractTravelsAlt),
      locale.fieldIsMandatory(locale.Travels)
    )
    .required(locale.fieldIsMandatory(locale.Travels)),
  MaxCost: yup.object().when("Type", {
    is: (t) => {
      return (
        t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowance ||
        t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt
      );
    },
    then: (s) => {
      return s.test(
        "MaxCost",
        locale.fieldIsMandatory(locale.MaxCost),
        (v) => v.Price > 0 && !isEmpty(v.Currency) && !isEmpty(v.Period)
      );
    }
  }),
  TravelTemplateValue: yup
    .string()
    .nullable(true)
    .when("Type", {
      is: (v) => {
        return v === ConsultantContractTravelsAlt.TravelTemplate;
      },
      then: (s) => s.required(locale.fieldIsMandatory(""))
    })
});

export const travelsSubSchemaBC = yup.object().shape({
  Type: yup.mixed(),
  MaxCost: yup.object().when("Type", {
    is: (t) => {
      return (
        t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowance ||
        t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt
      );
    },
    then: (s) => {
      return s.test(
        "MaxCost",
        locale.fieldIsMandatory(locale.MaxCost),
        (v) => v.Price > 0 && !isEmpty(v.Currency) && !isEmpty(v.Period)
      );
    }
  })
});

export const travelsSchemaBC = yup.object().shape({
  Type: yup.mixed(),
  MaxCost: yup.object().when("Type", {
    is: (t) => {
      return (
        t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowance ||
        t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt
      );
    },
    then: (s) => {
      return s.test(
        "MaxCost",
        locale.fieldIsMandatory(locale.MaxCost),
        (v) =>
          v.Price > 0 &&
          !isEmpty(v.Currency) &&
          !isEmpty(v.Period) &&
          !isEmpty(v.SalaryType)
      );
    }
  })
});

export const travels: FieldProps = field({
  xs: 12,
  name: "Travels",
  schema: yup.object().shape({
    Type: yup
      .mixed()
      .oneOf(
        Object.values(ConsultantContractTravelsAlt),
        locale.fieldIsMandatory(locale.Travels)
      )
      .required(locale.fieldIsMandatory(locale.Travels)),
    MaxCost: yup.object().when("Type", {
      is: (t) => {
        return (
          t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowance ||
          t === ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt
        );
      },
      then: (s) => {
        return s.test(
          "MaxCost",
          locale.fieldIsMandatory(locale.MaxCost),
          (v) =>
            v.Price > 0 &&
            !isEmpty(v.Currency) &&
            !isEmpty(v.Period) &&
            !isEmpty(v.SalaryType)
        );
      }
    }),
    TravelTemplateValue: yup
      .string()
      .nullable(true)
      .when("Type", {
        is: (v) => {
          return v === ConsultantContractTravelsAlt.TravelTemplate;
        },
        then: (s) => s.required(locale.fieldIsMandatory(""))
      })
  }),
  show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  render: (form, { store }) => {
    const type = form.values.Travels?.Type;
    const travelTemplateValue = form.values.Travels?.TravelTemplateValue;
    const showTravelTemplateValue =
      ([
        "5c5fd50af7ffb72545776834", // Region Norrbotten (Bonliva AB)
        "5c5fd50af7ffb72545776836", // Region Västernorrland (Bonliva AB)
        "5c5fd50af7ffb72545776820", // Region Västerbotten (Bonliva AB)
        "5c5fd50af7ffb7254577683e", // Region Jämtland Härjedalen (Bonliva AB)
        "682ae0cd2081fa75129a08cf", // Region Jämtland Härjedalen (WCC)
        "682ae092341be5b282ad1284", // Region Norrbotten (WCC)
        "682ae05ff3c6d1e488aad1d5", // Region Västerbotten (WCC)
        "682ae025341be5b282ad1269" // Region Västernorrland (WCC)
      ].includes(store.selectedCustomer?.Parents[0]?.Id) ||
        store.selectedCustomer?.Parents[0]?.Name.toLowerCase().includes("test")) &&
      [
        ConsultantContractType.Outsource_National,
        ConsultantContractType.Employment_National
      ].includes(form.values.ContractType);

    const isSubcontractor =
      store.currentConsultant?.LegalPersonType === LegalPersonType.Subcontractor;
    const options = getConsultantContractTravelsAltOptions(
      store.tenant,
      isSubcontractor,
      showTravelTemplateValue
    );
    const salaryTypeOpt =
      type === ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt &&
      isSubcontractor
        ? defaultSalaryTypeOpt
        : salaryTypeOption;
    const showMaxCostTravels =
      type === ConsultantContractTravelsAlt.BonlivaPayTravelAllowance ||
      type === ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt;
    const showTaxCompensInfo =
      type === ConsultantContractTravelsAlt.ConsultantTaxCompensationAllowance;

    return (
      <FormControl fullWidth key="Travels">
        <FormLabel component="legend">{locale.Travels}</FormLabel>

        <FormGroup>
          <RadioGroup name="Travels.Type" options={options} />
          {type === ConsultantContractTravelsAlt.TravelTemplate && (
            <Grid item xs={4}>
              <Text
                name={"Travels.TravelTemplateValue"}
                label={""}
                TextFieldProps={{
                  type: "number",
                  error: !!form.getError("Travels.TravelTemplateValue"),
                  helperText: `Ange summa som ska betalas ut till ${
                    isSubcontractor ? "leverantören" : "den anställde"
                  } efter varje två-veckorsperiod med dit- och hemresa: ${
                    travelTemplateValue ?? 0
                  } kr`
                }}
              />
            </Grid>
          )}
          {type === ConsultantContractTravelsAlt.BonlivaPayTravel && (
            <Grid item xs={12}>
              <Text
                name="Travels.PossibleSupplement"
                label={locale.PossibleSupplement}
                TextFieldProps={{
                  multiline: true,
                  helperText: locale["Press Enter for a new line"]
                }}
              />
            </Grid>
          )}
          {showMaxCostTravels && (
            <Grid item xs={6}>
              <MaxCost
                name="Travels.MaxCost"
                type="travels"
                error={form.getError("Travels.MaxCost")}
                contractVariant={store.currentConsultant?.LegalPersonType}
                options={salaryTypeOpt}
              />
            </Grid>
          )}
          {showTaxCompensInfo && (
            <Grid item xs={12}>
              <Typography color="textSecondary">
                {locale.consultantContractTaxCompens}
              </Typography>
            </Grid>
          )}
        </FormGroup>
      </FormControl>
    );
  }
});

export const dateFrameworkContractTypeIsSigned: FieldProps = field({
  xs: 8,
  name: "DateFrameworkContractTypeIsSigned",
  schema: yup.string().nullable(true).required(" "),
  render: (form, { store }) => {
    return (
      <Date
        utc
        key={"DateFrameworkContractTypeIsSigned"}
        DatePickerProps={{
          startDate: moment().toDate(),
          shouldCloseOnSelect: true,
          disabled: true,
          clearable: false,
          placeholder:
            store.currentConsultant?.LegalPersonType === LegalPersonType.Employee
              ? locale.fieldIsMandatory(locale.DateIntermittentAgreementIsSigned)
              : locale.fieldIsMandatory(locale.DateFrameworkAgreementIsSigned)
        }}
        name="DateFrameworkContractTypeIsSigned"
        label={
          store.currentConsultant?.LegalPersonType === LegalPersonType.Employee
            ? locale.DateIntermittentAgreementIsSigned
            : locale.DateFrameworkAgreementIsSigned
        }
      />
    );
  }
});

export const editsProof: FieldProps = field({
  xs: 8,
  name: "editsProof",
  schema: yup.object<any>().shape({
    approve: yup.bool(),
    files: yup.array().when("approve", {
      is: (v) => !!v,
      then: yup.array(),
      otherwise: yup.array().min(1, locale.fieldIsMandatory(""))
    })
  }),
  render: (form, { store: { requestUpload, canEditSignedWithoutProof } }) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const fileErr = !!form.errors.editsProof?.files;
    return (
      <FormSection
        // key={"editsProof"}
        flat
        fullWidth
        withBorder
        // PaperProps={{ style: { padding: 0 } }}
        title={"Redigering av fel:"}
      >
        {canEditSignedWithoutProof && (
          <Checkbox
            name={"editsProof.approve"}
            CheckboxProps={{
              style: { marginLeft: 16 }
            }}
            label={
              <WithTooltip
                title={
                  "Klicka för denna om du enbart redigerat ett fel, dvs att ingen egentlig förändring av avtalet har skett. Om ny överenskommelse har skett med konsult behöver så behöver du ladda upp en screen shot på denna."
                }
              >
                <Info style={{ marginBottom: -5 }} />
              </WithTooltip>
            }
          />
        )}
        <DnDLocalContainer
          style={{
            display: "flex",
            flex: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
            paddingTop: 0,
            paddingBottom: 0
          }}
          // overlayProps={{ className: classes.dndOverlay }}
          onDrop={(files) => form.push("editsProof.files", files)}
          noClick
        >
          <RegularButton
            variant="text"
            leftIcon={<FileUpload />}
            onClick={() =>
              requestUpload().then((files) => form.push("editsProof.files", files))
            }
            style={{
              margin: "8px auto",
              color: fileErr ? "#F65629" : "initial"
            }}
          >
            {"Ladda upp fil"}
          </RegularButton>
          <div style={{ width: "100%", padding: "0 16px" }}>
            <FileList
              files={form.values.editsProof?.files ?? []}
              onDelete={(i) => form.removeByIndex("editsProof.files", i)}
            />
          </div>
        </DnDLocalContainer>
      </FormSection>
    );
  }
});
